import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ActionIcon, Badge, Box, Button, Group, Text } from "@mantine/core";
import { IconUserPlus, IconX, IconPrinter } from "@tabler/icons-react";
import { useApp } from "../../../context/AppContext";
import { useClient } from "../../../context/ClientContext";
import usePermissions from "../../../hooks/usePermissions";
import {
  GiftCardLedgerEntry,
  GiftCardListItem,
} from "../../../types/giftCards";
import { Client } from "../../../types/client";
import { formatCurrency } from "../../../utils/cashDrawerUtils";
import { formatDateTime } from "../../../utils/timeUtils";
import { giftCardStatusColor } from "../../../utils/giftCardStatus";
import { giftCardRecipientName } from "../../../utils/giftCardRecipient";
import ClientLinkButton from "../../clients/ClientLinkButton";
import useGiftCardPrint from "../../common/useGiftCardPrint";
import AttachGiftCardToClientModal from "./AttachGiftCardToClientModal";
import { SectionLabel, KVRow } from "./panelParts";
import styles from "./sales.module.css";

const LEDGER_LABELS: Record<string, string> = {
  purchase: "Purchased",
  grant: "Granted",
  adjust: "Adjusted",
  redeem: "Redeemed",
  void: "Voided",
  breakage: "Breakage",
  claim: "Claimed",
};

interface GiftCardDetailDrawerProps {
  card: GiftCardListItem;
  salonId: string;
  /** IANA timezone of the business location; all datetimes render in it. */
  timezone: string;
  onClose: () => void;
  /** Reload the list after a management action changes the card. */
  onChanged: () => void;
}

/**
 * Gift-card detail slide-over for the Sales → Gift Cards tab. Read-rich (purchase,
 * value, people, audit history) plus, for an unclaimed card, a permission-gated
 * "Attach" action that assigns it to a client. The buyer + holder names are the
 * same `ClientLinkButton` the calendar transaction modal uses, so opening a
 * client profile is identical here.
 *
 * Rendered by portaling into the shared Sales drawer slot; reuses the sales
 * detail-panel CSS so it visually matches the Transactions/Payments drawers.
 */
export default function GiftCardDetailDrawer({
  card,
  salonId,
  timezone,
  onClose,
  onChanged,
}: GiftCardDetailDrawerProps) {
  const { getGiftCardLedger } = useApp();
  const { clientsById } = useClient() as {
    clientsById: Record<string, Client>;
  };
  const perms = usePermissions();
  const canManage = perms.can("giftCards", "canManageClientGiftCards");
  const { printCard, certificate } = useGiftCardPrint();

  // Print a certificate for this card. "To" is the intended recipient recorded
  // at sale time (shown even before the card is claimed); it falls back to the
  // attached holder, and omits the line entirely when neither is known.
  const handlePrint = () =>
    printCard({
      code: card.code,
      initialAmount: card.initial_amount,
      balance: card.balance,
      status: card.status,
      purchaserName: card.purchaser_name,
      recipientName:
        giftCardRecipientName(
          card.recipient_first_name,
          card.recipient_last_name,
        ) ?? card.client_name,
      giftMessage: card.gift_message,
      expiresAt: card.expires_at,
      purchasedAt: card.purchased_dt,
    });

  const [ledger, setLedger] = useState<GiftCardLedgerEntry[]>([]);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [attachOpen, setAttachOpen] = useState(false);

  // Resolve the full Client for the profile links (clientsById is keyed by the
  // client UUID). Falls back to the stored name when the client isn't cached.
  const buyer = card.purchaser_client_uuid
    ? clientsById[card.purchaser_client_uuid]
    : undefined;
  const holder = card.client_uuid ? clientsById[card.client_uuid] : undefined;

  const loadLedger = useCallback(async () => {
    if (!salonId) return;
    setLedgerLoading(true);
    try {
      setLedger(await getGiftCardLedger(salonId, card.id));
    } catch (err) {
      console.error("Failed to load gift card history:", err);
    } finally {
      setLedgerLoading(false);
    }
  }, [salonId, card.id, getGiftCardLedger]);

  useEffect(() => {
    loadLedger();
  }, [loadLedger]);

  const handleActionSuccess = useCallback(() => {
    onChanged();
    loadLedger();
  }, [onChanged, loadLedger]);

  const redeemed = (card.initial_amount || 0) - (card.balance || 0);
  // Only an unclaimed card (no holder) can be attached; attaching is a
  // management action, so it's gated on the same permission.
  const hasHolder = Boolean(card.client_uuid);
  const showAttach = canManage && !hasHolder;

  const purchasedMeta = useMemo(
    () =>
      card.purchased_dt
        ? formatDateTime(card.purchased_dt, timezone, "MMM D, YYYY · h:mm A")
        : "",
    [card.purchased_dt, timezone],
  );

  return (
    <Box className={styles.detailPanel}>
      <Box className={styles.panelHeader}>
        <Box style={{ minWidth: 0 }}>
          <Text className={styles.panelTitle}>Gift Card · {card.code}</Text>
          <Text className={styles.panelMeta}>{purchasedMeta}</Text>
        </Box>
        <Box style={{ flex: 1 }} />
        <Badge
          variant="light"
          color={giftCardStatusColor(card.status)}
          radius="sm"
          tt="capitalize"
        >
          {card.status}
        </Badge>
        <ActionIcon
          variant="transparent"
          color="gray"
          aria-label="Print gift card"
          onClick={handlePrint}
        >
          <IconPrinter size={16} stroke={2} color="#555" />
        </ActionIcon>
        <ActionIcon
          variant="transparent"
          color="gray"
          aria-label="Close detail"
          onClick={onClose}
        >
          <IconX size={16} stroke={2.4} color="#555" />
        </ActionIcon>
      </Box>

      <Box className={styles.panelBody}>
        <Box>
          <SectionLabel>Purchase</SectionLabel>
          <KVRow
            label="Transaction Id"
            value={card.transaction_reference_id || "—"}
          />
          <KVRow
            label="Purchase Method"
            value={card.source === "manual_grant" ? "Manual grant" : "Sale"}
          />
          <KVRow
            label="Original Value"
            value={formatCurrency(card.initial_amount)}
          />
          <KVRow label="Purchased" value={purchasedMeta || "—"} />
        </Box>

        <Box>
          <SectionLabel>Value</SectionLabel>
          <KVRow
            label="Remaining Balance"
            value={formatCurrency(card.balance)}
          />
          <KVRow label="Redeemed" value={formatCurrency(redeemed)} />
          <KVRow
            label="Expires"
            value={
              card.expires_at
                ? formatDateTime(card.expires_at, timezone, "MMM D, YYYY")
                : "No expiry"
            }
          />
        </Box>

        <Box>
          <SectionLabel>People</SectionLabel>
          <Box className={styles.kvRow}>
            <Text component="span" className={styles.kvLabel}>
              Purchaser
            </Text>
            {buyer ? (
              <ClientLinkButton client={buyer} />
            ) : (
              <Text component="span" className={styles.kvValue}>
                {card.purchaser_name || "—"}
              </Text>
            )}
          </Box>
          <Box className={styles.kvRow}>
            <Text component="span" className={styles.kvLabel}>
              Attached to
            </Text>
            {hasHolder ? (
              holder ? (
                <ClientLinkButton client={holder} />
              ) : (
                <Text component="span" className={styles.kvValue}>
                  {card.client_name || "—"}
                </Text>
              )
            ) : (
              <Group gap="xs" wrap="nowrap">
                <Text component="span" className={styles.kvValue} c="dimmed">
                  Unclaimed
                </Text>
                {showAttach && (
                  <Button
                    variant="light"
                    color="var(--brand-teal)"
                    size="compact-xs"
                    radius={7}
                    leftSection={<IconUserPlus size={13} />}
                    onClick={() => setAttachOpen(true)}
                  >
                    Attach
                  </Button>
                )}
              </Group>
            )}
          </Box>
          {card.claimed_dt && (
            <KVRow
              label="Claimed"
              value={formatDateTime(card.claimed_dt, timezone, "MMM D, YYYY")}
            />
          )}
        </Box>

        <Box>
          <SectionLabel count={ledger.length}>History</SectionLabel>
          {ledgerLoading && ledger.length === 0 ? (
            <Text size="sm" className={styles.cellMuted}>
              Loading…
            </Text>
          ) : ledger.length === 0 ? (
            <Text size="sm" className={styles.cellMuted}>
              No activity yet.
            </Text>
          ) : (
            ledger.map((entry) => (
              <Box key={entry.id} className={styles.kvRow}>
                <Text component="span" className={styles.kvLabel}>
                  {entry.created_dt
                    ? formatDateTime(entry.created_dt, timezone, "MMM D, YYYY")
                    : ""}{" "}
                  · {LEDGER_LABELS[entry.entry_type] ?? entry.entry_type}
                </Text>
                <Text component="span" className={styles.kvValue}>
                  {entry.delta_amount != null
                    ? formatCurrency(entry.delta_amount, { showSign: true })
                    : "—"}
                </Text>
              </Box>
            ))
          )}
        </Box>
      </Box>

      {attachOpen && (
        <AttachGiftCardToClientModal
          opened
          onClose={() => setAttachOpen(false)}
          salonId={salonId}
          giftCardId={card.id}
          code={card.code}
          onSuccess={() => {
            handleActionSuccess();
            setAttachOpen(false);
          }}
        />
      )}

      {certificate}
    </Box>
  );
}
